/**
 * Forgot Password Page Component
 * Allows users to request a password reset link via email
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form, Input, Button, Alert, Typography } from 'antd';
import { MailOutlined, ArrowLeftOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { forgotPassword } from '../../services/authService';
import PagesHeader from '../../PagesHeader';
import styles from './ForgotPassword.module.css';

const { Title, Text } = Typography;

/**
 * Forgot Password page - request password reset link
 */
const ForgotPassword = () => {
  const { t } = useTranslation();
  
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  /**
   * Handle form submission
   */
  const handleSubmit = async (values) => {
    setSubmitting(true);
    setError(null);

    try {
      await forgotPassword(values.email);
      setSubmittedEmail(values.email);
      setEmailSent(true);
    } catch (err) {
      // For security, we show success even if email doesn't exist
      // The backend should handle this the same way
      setSubmittedEmail(values.email);
      setEmailSent(true);
    } finally {
      setSubmitting(false);
    }
  };

  // Show success message after submission
  if (emailSent) {
    return (
      <div className={styles.pageContainer}>
        <PagesHeader />
        <main className={styles.main}>
          <div className={styles.card}>
            <div className={styles.successContainer}>
              <CheckCircleOutlined className={styles.successIcon} />
              <Title level={3} className={styles.successTitle}>
                {t('auth.checkYourEmail')}
              </Title>
              <Text className={styles.successText}>
                {t('auth.resetLinkSent', { email: submittedEmail })}
              </Text>
              <Text className={styles.successHint}>
                {t('auth.checkSpamFolder')}
              </Text>
              <div className={styles.successActions}>
                <Link to="/login">
                  <Button type="primary" size="large" className={styles.submitButton}>
                    {t('auth.backToLogin')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <PagesHeader />
      
      <main className={styles.main}>
        <div className={styles.card}>
          <Link to="/login" className={styles.backLink}>
            <ArrowLeftOutlined /> {t('auth.backToLogin')}
          </Link>

          <div className={styles.header}>
            <Title level={2} className={styles.title}>
              {t('auth.forgotPasswordTitle')}
            </Title>
            <Text type="secondary" className={styles.subtitle}>
              {t('auth.forgotPasswordSubtitle')}
            </Text>
          </div>

          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              onClose={() => setError(null)}
              className={styles.alert}
            />
          )}

          <Form
            form={form}
            name="forgotPassword"
            onFinish={handleSubmit}
            layout="vertical"
            requiredMark={false}
            className={styles.form}
          >
            <Form.Item
              name="email"
              label={t('auth.email')}
              rules={[
                { required: true, message: t('auth.emailRequired') },
                { type: 'email', message: t('auth.emailInvalid') }
              ]}
            >
              <Input
                prefix={<MailOutlined className={styles.inputIcon} />}
                placeholder={t('auth.emailPlaceholder')}
                size="large"
                autoComplete="email"
                disabled={submitting}
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={submitting}
                className={styles.submitButton}
              >
                {submitting ? t('auth.sending') : t('auth.sendResetLink')}
              </Button>
            </Form.Item>
          </Form>

          <div className={styles.helpText}>
            <Text type="secondary">
              {t('auth.rememberPassword')}{' '}
              <Link to="/login" className={styles.link}>
                {t('auth.signIn')}
              </Link>
            </Text>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ForgotPassword;
